import { useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Package, PencilSimple, PaperPlaneTilt, Trash, Check, Warning, FirstAidKit } from "@phosphor-icons/react";
import { StatusBadge, fmtMoney } from "./shared";

export default function ItemDetailDialog({ item, checklist, onClose, onChanged, onEdit, onSend, onDeleted }) {
  const [notes, setNotes] = useState(item?.notes || "");
  const [busy, setBusy] = useState(false);
  if (!item) return null;
  const d = item.draft || {};
  const eq = item.equipment || {};
  const r = item.readiness || { filled: 0, total: 1, missing: [] };
  const sent = !!item.sent;
  const photo = d.photos_url || (d.photos && d.photos[0]);

  const toggle = async (key, val) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/preinventory/${item.id}`, { checklist: { [key]: val } });
      onChanged?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    } finally { setBusy(false); }
  };

  const saveNotes = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/preinventory/${item.id}`, { notes });
      onChanged?.(data);
      toast.success("Notas guardadas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(false); }
  };

  const discard = async () => {
    if (!window.confirm(`¿Descartar la ficha ${item.code}? ${item.patient_code ? "El paciente regresará a Segunda revisión." : ""}`)) return;
    setBusy(true);
    try {
      await api.delete(`/preinventory/${item.id}`);
      toast.success("Ficha descartada");
      onDeleted?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo descartar");
    } finally { setBusy(false); }
  };

  const specEntries = Object.entries(d.specs || {}).filter(([, v]) => v);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="preinventory-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[#002FA7]">{item.code}</span>
            {item.patient_code && (
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-800 border border-cyan-200">
                <FirstAidKit size={11} weight="bold" /> {item.patient_code}
              </span>
            )}
            <span className="truncate">{d.name || "Sin nombre"}</span>
            <StatusBadge status={item.status} />
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-4">
            <div className="w-[120px] h-[120px] rounded-md bg-zinc-100 border border-zinc-200 overflow-hidden flex items-center justify-center">
              {photo ? <img src={photo} alt="" className="w-full h-full object-cover" /> : <Package size={28} className="text-zinc-300" />}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              <Field label="Categoría" value={d.category} />
              <Field label="Marca" value={d.brand} />
              <Field label="SKU" value={d.sku} mono />
              <Field label="Piezas" value={d.stock} mono />
              <Field label="Costo" value={fmtMoney(d.cost, d.cost_currency)} mono />
              <Field label="Precio tienda" value={fmtMoney(d.store_price, d.sale_currency)} mono strong />
              <Field label="Mínimo" value={fmtMoney(d.min_price, d.sale_currency)} mono />
              <Field label="Reventa" value={fmtMoney(d.reseller_price, d.sale_currency)} mono />
              <Field label="Distribuidor" value={fmtMoney(d.marketplace_price, d.sale_currency)} mono />
            </div>
          </div>

          {/* Readiness */}
          <section className="border border-zinc-200 rounded-md p-3 bg-zinc-50" data-testid="detail-readiness">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Preparación para inventario</div>
              <div className="flex items-center gap-2">
                <div className="w-40 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                  <div className={`h-full ${r.ready ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.round((r.filled / r.total) * 100)}%` }} />
                </div>
                <span className="text-xs font-mono-data font-semibold text-zinc-700">{r.filled}/{r.total}</span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
              {checklist.map((c) => {
                const on = !!item.checklist?.[c.key];
                return (
                  <label key={c.key} className={`flex items-center gap-2 px-3 py-2 rounded-md border bg-white text-sm cursor-pointer ${on ? "border-emerald-200" : "border-zinc-200"}`} data-testid={`detail-check-${c.key}`}>
                    <Checkbox checked={on} disabled={sent || busy} onCheckedChange={(v) => toggle(c.key, !!v)} />
                    <span className={on ? "text-emerald-800 font-medium" : "text-zinc-700"}>{c.label}</span>
                  </label>
                );
              })}
            </div>
            {r.missing.length > 0 && !sent && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 self-center inline-flex items-center gap-1"><Warning size={11} weight="bold" className="text-amber-600" /> Falta:</span>
                {r.missing.map((m) => (
                  <span key={m.key} className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-amber-100 text-amber-700">{m.label}</span>
                ))}
              </div>
            )}
            {sent && (
              <div className="mt-3 text-sm text-emerald-800 inline-flex items-center gap-1.5" data-testid="detail-sent-info">
                <Check size={14} weight="bold" />
                {item.sent.mode === "entry" ? `Entrada de +${item.sent.quantity} registrada en «${item.sent.product_name}»` : `Creado como producto «${item.sent.product_name}»`}
                <span className="text-zinc-500">· {new Date(item.sent.at).toLocaleString("es-MX")} · {item.sent.by_name}</span>
              </div>
            )}
          </section>

          {/* Specs + storefront */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">Specs</div>
              {specEntries.length === 0 ? <div className="text-xs text-zinc-400 italic">Sin specs</div> : (
                <div className="flex flex-wrap gap-1.5">
                  {specEntries.map(([k, v]) => (
                    <span key={k} className="text-xs bg-white border border-zinc-200 rounded px-2 py-1"><span className="text-zinc-500 uppercase text-[10px] mr-1">{k}</span>{v}</span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">Tienda</div>
              <div className="text-xs text-zinc-700 space-y-1">
                <div>Condición: <b className="capitalize">{d.condition || "—"}</b> · Estética: <b>{d.aesthetic_grade || "—"}</b></div>
                <div>Casos de uso: {(d.use_cases || []).length ? d.use_cases.join(", ") : <span className="italic text-zinc-400">ninguno</span>}</div>
                <div>Fotos: <b>{(d.photos || []).filter(Boolean).length}</b> · Slogan: {d.short_tagline || <span className="italic text-zinc-400">—</span>}</div>
              </div>
            </div>
          </section>

          {d.characteristics && (
            <section>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">Características</div>
              <div className="text-sm text-zinc-700 whitespace-pre-wrap bg-white border border-zinc-200 rounded-md p-3">{d.characteristics}</div>
            </section>
          )}

          {item.equipment && (
            <section className="border border-cyan-200 bg-cyan-50/40 rounded-md p-3" data-testid="detail-equipment">
              <div className="text-[10px] uppercase tracking-widest text-cyan-800 font-semibold mb-2 inline-flex items-center gap-1"><FirstAidKit size={11} weight="bold" /> Expediente Hospital Interno</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-zinc-700">
                <Field label="Equipo" value={[eq.equipment_type, eq.equipment_subtype].filter(Boolean).join(" · ")} />
                <Field label="Modelo" value={eq.model} />
                <Field label="Serie" value={eq.serial_number} mono />
                <Field label="Estética" value={eq.aesthetic_condition} />
                <Field label="Recibido" value={eq.received_at ? new Date(eq.received_at).toLocaleDateString("es-MX") : ""} />
                <Field label="Técnico" value={eq.technician_name} />
                <Field label="Proveedor" value={eq.supplier} />
                <Field label="Lote" value={eq.batch_number} />
              </div>
              {(eq.damages || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {eq.damages.map((dm, i) => (
                    <span key={i} className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded px-2 py-0.5"><b>{dm.kind}</b>{dm.description ? ` — ${dm.description}` : ""}</span>
                  ))}
                </div>
              )}
            </section>
          )}

          <section>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1.5">Notas de preparación</div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} disabled={sent} placeholder="Ej. se cambió pasta térmica, falta foto lateral…" data-testid="detail-notes" />
            {!sent && notes !== (item.notes || "") && (
              <Button size="sm" variant="outline" onClick={saveNotes} disabled={busy} className="mt-2" data-testid="detail-save-notes">Guardar notas</Button>
            )}
          </section>
        </div>

        <DialogFooter className="gap-2 sm:justify-between flex-wrap">
          {!sent ? (
            <Button variant="outline" onClick={discard} disabled={busy} className="gap-1.5 border-[#E0000F]/40 text-[#E0000F] hover:bg-[#E0000F]/5" data-testid="detail-discard">
              <Trash size={14} weight="bold" /> Descartar ficha
            </Button>
          ) : <span />}
          <div className="flex gap-2">
            {!sent && (
              <Button variant="outline" onClick={() => onEdit?.(item)} className="gap-1.5" data-testid="detail-edit">
                <PencilSimple size={14} weight="bold" /> Editar ficha completa
              </Button>
            )}
            {!sent && (
              <Button onClick={() => onSend?.(item)} className="bg-[#002FA7] hover:bg-[#002277] text-white gap-1.5" data-testid="detail-send">
                <PaperPlaneTilt size={14} weight="bold" /> Enviar a Inventario
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, mono, strong }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-0.5 ${mono ? "numeric" : ""} ${strong ? "font-semibold text-[#002FA7]" : "text-zinc-900"}`}>{value || "—"}</div>
    </div>
  );
}
